import { Card, CardBody, Chip } from "@nextui-org/react";
import { useEffect, useState } from "react";
import { getDataWithFilterUrl } from "../../controllers/APIController";
import { useNavigate, useParams } from "react-router-dom";

// Color mapping for different categories
const categoryColorMap = {
  social: "primary",
  Environment: "success",
  survey: "default",
  governance: "danger",
  "Reference Data": "secondary",
  "Vendor details": "default",
};

/**
 * SingleSearchComponent Component
 * Individual search result item displaying metric or evidence information
 * Handles navigation and displays relevant metadata
 * @param {Object} props - Component props
 * @param {Object} props.data - Search result data object
 * @returns {JSX.Element} Individual search result component
 */
const SingleSearchComponent = ({ data }) => {
  // Component state
  const [responseCount, setResponseCount] = useState(0);
  
  // Navigation and routing hooks
  const navigate = useNavigate();
  const { _orgId } = useParams();

  // User and organization data from session storage
  const userLogged = JSON.parse(sessionStorage.getItem("userData"));
  const orgInfo = userLogged && userLogged.user.LastOrgId;
  const organizationFilterUrl = "filters[Organization][id][$eqi]=" + orgInfo;

  /**
   * Handle navigation based on data type
   * Routes to appropriate page or opens evidence in new tab
   * @param {Object} itemData - Data object containing navigation information
   */
  const handleNavigation = (itemData) => {
    let navigationPath = "";
    
    if (itemData.type === "Evidence") {
      // Open evidence file in new tab
      navigationPath = process.env.REACT_APP_STRAPI_IP_ADDRESS + itemData?.url;
      window.open(navigationPath);
    } else if (itemData?.category?.trim()?.toLowerCase() === "reference data") {
      // Navigate to reference data page
      navigationPath =
        `/${_orgId}/` +
        itemData.category
          .toLowerCase()
          .replace(/[\s_]+/g, "-")
          .replace(/-+/g, "-")
          .trim() +
        "/" +
        itemData.Name.toLowerCase()
          .replace(/[\s_]+/g, "-")
          .replace(/-+/g, "-")
          .trim();
      navigate(navigationPath);
    } else {
      // Navigate to metrics page
      navigationPath =
        `/${_orgId}` +
        "/metrics/" +
        itemData.category
          .toLowerCase()
          .replace(/[\s_]+/g, "-")
          .replace(/-+/g, "-")
          .trim() +
        "/" +
        itemData.Name.toLowerCase()
          .replace(/[\s_]+/g, "-")
          .replace(/-+/g, "-")
          .trim();
      navigate(navigationPath);
    }
  };

  /**
   * Fetch response count for metrics on component mount
   * Only fetches for metric types, not evidence
   */
  useEffect(() => {
    if (data?.type === "Evidence") return;
    
    const responseFilterUrl = organizationFilterUrl + "&filters[Metric_Id][$eqi]=" + data?.Name;

    getDataWithFilterUrl("responses", responseFilterUrl)
      .then((responseData) => {
        setResponseCount(responseData?.data?.length || 0);
      })
      .catch((error) => {
        console.error("Error fetching response count:", error);
      });
  }, [data?.Name, data?.type, organizationFilterUrl]);

  return (
    <Card
      shadow="none"
      className="my-8 border-[1.5px] border-solid border-gray-light cursor-pointer"
    >
      <CardBody onClick={() => handleNavigation(data)}>
        <div className="flex flex-col">
          {/* Item Header with Icon and Title */}
          <div className="flex">
            {/* Dynamic Icon based on item type */}
            {data.type === "Evidence" ? (
              <lord-icon
                src="https://cdn.lordicon.com/rbbnmpcf.json"
                trigger="hover"
                target="#evidences"
                state="morph-home-2"
                style={{ width: "40px", height: "40px" }}
              />
            ) : (
              <lord-icon
                src="https://cdn.lordicon.com/nizfqlnk.json"
                trigger="hover"
                target="#Metrics"
                state="morph-home-2"
                style={{ width: "40px", height: "40px" }}
              />
            )}
            
            {/* Item Title and Type */}
            <div className="flex items-start justify-between mx-2 w-full">
              <div className="flex flex-col justify-between w-full">
                <span className="font-medium text-lg">{data?.Name}</span>
                <span className="text-sm text-gray font-medium mr-8">
                  {data?.type || "--"}
                </span>
              </div>
            </div>
          </div>
          
          {/* Metric-specific Information */}
          {data?.type === "Metric" && (
            <div className="my-2">
              <div className="flex items-center gap-2 my-2">
                {/* Category Chip */}
                <Chip
                  color={categoryColorMap[data?.category || "default"]}
                  variant="flat"
                  size="sm"
                >
                  {data?.category}
                </Chip>
                
                {/* Tag Chips */}
                {data?.TagList?.map((tag, index) => (
                  <Chip
                    key={`tag-${index}`}
                    color={categoryColorMap[data?.category || "default"]}
                    variant="flat"
                    size="sm"
                  >
                    {tag}
                  </Chip>
                ))}

                {/* Response Count Chip */}
                <Chip color="warning" variant="flat" size="sm">
                  Number of Responses: {responseCount}
                </Chip>
              </div>
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

export default SingleSearchComponent;
